/**
 * ============================================================================
 * SignupSuccessPage.js - Post-Free-Signup Welcome Landing
 * ============================================================================
 * Created: May 27, 2026 - Step 3 Free + Starter Tier Rollout
 *
 * Lands here after a successful Free-tier signup via RegisterPage. The Free
 * path does NOT go through Stripe (no /checkout/success). This page is the
 * Free-tier equivalent of CheckoutSuccessPage:
 *   - Confirms the account is active
 *   - Fires the `registration_completed` GA4 event (NOT `trial_started` —
 *     that event is reserved for paid trials, which Free has none of)
 *   - Provides a single "Go to Dashboard" CTA
 *
 * The `registration_completed` event is the same event paid signups fire
 * from RegisterPage before Stripe redirect. Firing it HERE for Free means
 * we count account creation at the moment it actually becomes a working
 * account (no Stripe gate to pass first). useRef guard prevents double-
 * firing on React re-renders.
 *
 * Visual style matches RegisterPage: green gradient background, centered
 * white card, ⛳ logo. Consistency with the page they just came from.
 * ============================================================================
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// GA4 tracking helper - safe to call even if gtag isn't loaded
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

function SignupSuccessPage() {
  const navigate = useNavigate();
  const eventFired = useRef(false);

  // Fire `registration_completed` exactly once on mount.
  // useRef guard prevents double-firing on React StrictMode re-renders
  // (same pattern CheckoutSuccessPage uses for `trial_started`).
  useEffect(() => {
    if (!eventFired.current) {
      trackEvent('registration_completed', {
        tier: 'free',
        billing_cycle: null,
      });
      console.log('📊 registration_completed fired (free tier)');
      eventFired.current = true;
    }
  }, []);

  const goToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
      padding: 24,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 40,
        width: '100%',
        maxWidth: 480,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        textAlign: 'center',
      }}>
        {/* Success icon */}
        <div style={{
          width: 80,
          height: 80,
          background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          fontSize: 44,
        }}>
          🎉
        </div>

        <h1 style={{
          fontSize: 26,
          fontWeight: 700,
          color: '#1f2937',
          margin: '0 0 12px 0',
        }}>
          Welcome to Tee Time Organizer!
        </h1>

        <p style={{
          fontSize: 16,
          color: '#374151',
          lineHeight: 1.6,
          margin: '0 0 8px 0',
        }}>
          Your <strong>Free</strong> account is active.
        </p>

        <p style={{
          fontSize: 14,
          color: '#6b7280',
          lineHeight: 1.6,
          margin: '0 0 28px 0',
        }}>
          You're all set for up to 8 players. Upgrade anytime to unlock pairings,
          scoring, SMS reminders, and more.
        </p>

        {/* What's included reminder */}
        <div style={{
          background: '#f9fafb',
          borderRadius: 10,
          padding: 16,
          marginBottom: 24,
          textAlign: 'left',
        }}>
          <div style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#374151',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginBottom: 10,
            textAlign: 'center',
          }}>
            Included with Free
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '6px 12px',
            fontSize: 13,
            color: '#4b5563',
          }}>
            <div>✓ Email reminders</div>
            <div>✓ Weather forecasts</div>
            <div>✓ Tee sheets</div>
            <div>✓ Waitlist management</div>
            <div>✓ One-click RSVP</div>
            <div>✓ Player database</div>
          </div>
        </div>

        {/* Primary CTA */}
        <button
          onClick={goToDashboard}
          style={{
            width: '100%',
            padding: '16px',
            background: '#22c55e',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
          }}
          onMouseEnter={(e) => e.target.style.opacity = '0.9'}
          onMouseLeave={(e) => e.target.style.opacity = '1'}
        >
          Go to My Dashboard →
        </button>

        <p style={{
          fontSize: 12,
          color: '#9ca3af',
          marginTop: 14,
          marginBottom: 0,
        }}>
          You'll see your dashboard in just a moment.
        </p>
      </div>
    </div>
  );
}

export default SignupSuccessPage;
